import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Badge, Pagination } from 'react-bootstrap';
import { useSearchParams, Link } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import searchService from '../services/searchService';
import { trackSearchEvent } from '../components/GoogleAnalytics';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);

  const query = searchParams.get('q') || '';
  const pageSize = 10;

  useEffect(() => {
    if (query.trim()) {
      searchContent(query, 0);
      setCurrentPage(0);
    }
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchContent = async (searchQuery, page) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await searchService.searchPublicContent(searchQuery, page, pageSize);
      setResults(response.data);
      
      // Track search event
      trackSearchEvent(searchQuery, response.data.totalElements);
    } catch (error) {
      console.error('Error searching content:', error);
      setError('Failed to search content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    searchContent(query, page);
    window.scrollTo(0, 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'survey':
        return 'fas fa-poll';
      case 'blog':
        return 'fas fa-blog';
      default:
        return 'fas fa-file';
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'survey':
        return <Badge bg="primary">Survey</Badge>;
      case 'blog':
        return <Badge bg="success">Blog</Badge>;
      default:
        return <Badge bg="secondary">{type}</Badge>;
    }
  };

  const highlightSearchTerm = (text, searchTerm) => {
    if (!text || !searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? 
        <mark key={index} className="bg-warning">{part}</mark> : part
    );
  };

  if (!query.trim()) {
    return (
      <Container className="mt-4">
        <Row>
          <Col>
            <div className="text-center py-5">
              <i className="fas fa-search fa-4x text-muted mb-3"></i>
              <h3>Search Our Website</h3>
              <p className="text-muted mb-4">
                Find surveys, blog posts, and other public content
              </p>
              <div className="col-md-6 mx-auto">
                <SearchBar placeholder="Enter your search terms..." />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          {/* Search Header */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2>
                <i className="fas fa-search me-2 text-primary"></i>
                Search Results
              </h2>
              <Link to="/" className="btn btn-outline-primary">
                <i className="fas fa-home me-2"></i>
                Home
              </Link>
            </div>
            
            {/* Search Bar */}
            <div className="col-md-8">
              <SearchBar />
            </div>
            
            {/* Results Summary */}
            {results && (
              <div className="mt-3">
                <p className="text-muted">
                  {results.totalElements > 0 ? (
                    <>
                      Showing {results.numberOfElements} of {results.totalElements} results for 
                      <strong> "{query}"</strong>
                    </>
                  ) : (
                    <>No results found for <strong>"{query}"</strong></>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Searching...</p>
            </div>
          )}

          {/* Error State */}
          {error && <Alert variant="danger">{error}</Alert>}

          {/* Results */}
          {results && !loading && (
            <>
              {results.content.length === 0 ? (
                <Card>
                  <Card.Body className="text-center py-5">
                    <i className="fas fa-search fa-3x text-muted mb-3"></i>
                    <h4>No Results Found</h4>
                    <p className="text-muted mb-3">
                      We couldn't find any content matching your search.
                    </p>
                    <div className="mb-3">
                      <strong>Try:</strong>
                      <ul className="list-unstyled mt-2">
                        <li>• Using different keywords</li>
                        <li>• Checking your spelling</li>
                        <li>• Using more general terms</li>
                        <li>• Browsing our <Link to="/surveys">surveys</Link> or <Link to="/blog">blog</Link></li>
                      </ul>
                    </div>
                  </Card.Body>
                </Card>
              ) : (
                <>
                  {/* Search Results */}
                  {results.content.map((result, index) => (
                    <Card key={index} className="mb-3 search-result-card">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center">
                            <i className={`${getTypeIcon(result.type)} me-2 text-primary`}></i>
                            {getTypeBadge(result.type)}
                          </div>
                          <small className="text-muted">
                            {formatDate(result.createdAt)}
                          </small>
                        </div>
                        
                        <h5 className="card-title">
                          <Link to={result.url} className="text-decoration-none">
                            {highlightSearchTerm(result.title, query)}
                          </Link>
                        </h5>
                        
                        {result.snippet && (
                          <p className="card-text text-muted">
                            {highlightSearchTerm(result.snippet, query)}
                          </p>
                        )}
                        
                        <div className="d-flex justify-content-between align-items-center">
                          <Link to={result.url} className="btn btn-outline-primary btn-sm">
                            <i className="fas fa-external-link-alt me-1"></i>
                            View {result.type === 'survey' ? 'Survey' : 'Content'}
                          </Link>
                          <small className="text-muted">
                            {result.url}
                          </small>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}

                  {/* Pagination */}
                  {results.totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <Pagination>
                        <Pagination.First 
                          disabled={currentPage === 0}
                          onClick={() => handlePageChange(0)}
                        />
                        <Pagination.Prev 
                          disabled={currentPage === 0}
                          onClick={() => handlePageChange(currentPage - 1)}
                        />
                        
                        {[...Array(Math.min(5, results.totalPages))].map((_, index) => {
                          const pageNum = Math.max(0, Math.min(
                            currentPage - 2 + index, 
                            results.totalPages - 5 + index
                          ));
                          
                          if (pageNum >= results.totalPages) return null;
                          
                          return (
                            <Pagination.Item
                              key={pageNum}
                              active={pageNum === currentPage}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum + 1}
                            </Pagination.Item>
                          );
                        })}
                        
                        <Pagination.Next 
                          disabled={currentPage >= results.totalPages - 1}
                          onClick={() => handlePageChange(currentPage + 1)}
                        />
                        <Pagination.Last 
                          disabled={currentPage >= results.totalPages - 1}
                          onClick={() => handlePageChange(results.totalPages - 1)}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Col>
      </Row>

      <style jsx>{`
        .search-result-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border: 1px solid #dee2e6;
        }
        
        .search-result-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        .search-result-card .card-title a {
          color: #495057;
        }
        
        .search-result-card .card-title a:hover {
          color: #007bff;
        }
        
        mark {
          padding: 0.1em 0.2em;
          border-radius: 0.2em;
        }
      `}</style>
    </Container>
  );
};

export default SearchResults;